/**
 * Widget Data Composable
 * Provides reactive data fetching for widgets with shared data sources
 */

import { ref, computed, onMounted, watch, nextTick, inject } from 'vue'
import { dataFetcher } from '../services/dataFetcher.js'
import { useDemoMode } from './useDemoMode.js'
import { getDateRangeTimestamps, getPreviousPeriodTimestamps } from '../utils/dateRanges.js'
import { 
  getDemoScheduleData, 
  getDemoUsersData, 
  getDemoTimeSavedData,
  getDemoRecentAutomationEnrollmentsData,
  getDemoCustomAutomationsData,
  getDemoActionsThisPeriodData,
  getDemoCheckInsData,
  getDemoAccountPlansData,
  getDemoDashboardKPIsData,
  getDemoTopAccountsByRevenueData,
  getDemoPlanRevenueData,
  getDemoInvoicesByStatusData,
  getDemoInvoicesPaidData,
  getDemoRevenueCollectedData,
  getDemoUnpaidInvoicesData,
  getDemoCreditOwedData
} from '../services/demoData.js'

/**
 * Returns the preview-mode limit for a given default limit.
 * Must be called during component setup (uses inject).
 * When inside AddWidgetPanel, returns a smaller limit to reduce API payload.
 */
export function usePreviewLimit(defaultLimit) {
  const isPreview = inject('widgetPreviewMode', false)
  return isPreview ? Math.min(defaultLimit, 3) : defaultLimit
}

// Shared data stores - multiple widgets can subscribe to the same data source
const sharedDataStores = new Map()

// Cached org currency promise - org currency is stable for the session, so we only fetch it once.
let orgCurrencyPromise = null

const ORG_CURRENCY_FALLBACK = { currency: 'USD', currency_symbol: '$', locale: 'en-US' }

/**
 * Fetches the organization's currency, symbol, and locale via analyticsQuery.
 * Result is cached for the session. On failure returns the USD fallback.
 * Pass priority/batch options through so it can ride along with another widget fetch.
 * @returns {Promise<{currency: string, currency_symbol: string, locale: string}>}
 */
export function fetchOrgCurrency({ priority = 1, enableBatch = true, batchGroup } = {}) {
  if (orgCurrencyPromise) return orgCurrencyPromise

  const query = `
    query analytics($entities: [AnalyticsQueryEntityInput], $fields: [AnalyticsQueryFieldInput], $page: Int, $per_page: Int) {
      analyticsQuery(query: {entities: $entities, fields: $fields, page: $page, per_page: $per_page}) {
        currency
        currency_symbol
        locale
      }
    }
  `
  const variables = {
    entities: [{ entity: 'INVOICE', alias: null }],
    fields: [{ name: 'INVOICE.invoice_id', aggregate: 'COUNT', alias: null }],
    page: 1,
    per_page: 1
  }

  orgCurrencyPromise = dataFetcher
    .fetchGraphQL(query, variables, 'analytics', priority, enableBatch, batchGroup)
    .then((result) => ({
      currency: result?.analyticsQuery?.currency || ORG_CURRENCY_FALLBACK.currency,
      currency_symbol: result?.analyticsQuery?.currency_symbol || ORG_CURRENCY_FALLBACK.currency_symbol,
      locale: result?.analyticsQuery?.locale || ORG_CURRENCY_FALLBACK.locale
    }))
    .catch(() => {
      // Reset the cache on failure so the next caller can retry rather than being stuck with the fallback.
      orgCurrencyPromise = null
      return { ...ORG_CURRENCY_FALLBACK }
    })

  return orgCurrencyPromise
}

/**
 * Clears the cached org currency. Call this when switching orgs or after a hard refresh.
 */
export function resetOrgCurrencyCache() {
  orgCurrencyPromise = null
}

// Cached organization id. The transactions query requires an organization_id, unlike
// the auth-scoped invoices/schedule queries, so we resolve it once and reuse it.
let orgIdPromise = null
export function fetchOrgId({ priority = 1, enableBatch = true, batchGroup } = {}) {
  if (orgIdPromise) return orgIdPromise

  const query = `
    query GetOrgId {
      organization {
        organization_id
      }
    }
  `

  orgIdPromise = dataFetcher
    .fetchGraphQL(query, {}, 'GetOrgId', priority, enableBatch, batchGroup)
    .then((result) => result?.organization?.organization_id ?? null)
    .catch(() => {
      orgIdPromise = null
      return null
    })

  return orgIdPromise
}

/**
 * Clears the cached org id. Call this when switching orgs or after a hard refresh.
 */
export function resetOrgIdCache() {
  orgIdPromise = null
}

/**
 * Creates or gets a shared data store
 * @param {string} dataKey - Unique key for the data source
 * @returns {Object} Shared data store
 */
function getSharedStore(dataKey) {
  if (!sharedDataStores.has(dataKey)) {
    const store = {
      data: ref(null),
      loading: ref(false),
      error: ref(null),
      subscribers: new Set(),
      fetchFn: null,
      lastFetch: null
    }
    sharedDataStores.set(dataKey, store)
  }
  return sharedDataStores.get(dataKey)
}

/**
 * Refreshes all shared data stores
 * @param {Object} [options] - Options for the refresh
 * @param {boolean} [options.showLoading=false] - When true, set loading state before fetch (e.g. for trial mode switch)
 * @returns {Promise<void>} Resolves when all fetches complete (when showLoading is true)
 */
export function refreshAllWidgetData(options = {}) {
  const { showLoading = false } = options

  // Invalidate cache to force fresh data
  dataFetcher.invalidateCache()
  resetOrgCurrencyCache()

  const promises = []
  sharedDataStores.forEach((store) => {
    if (store.fetchFn && store.subscribers.size > 0) {
      if (showLoading) {
        store.loading.value = true
      }
      promises.push(store.fetchFn())
    }
  })

  if (showLoading && promises.length > 0) {
    return Promise.all(promises).then(() => {})
  }
}

/**
 * Composable for widget data fetching
 * @param {Object} options - Configuration options
 * @param {string} options.dataKey - Unique key for this data source (for sharing between widgets)
 * @param {Function} options.fetchFn - Function that returns a promise for fetching data
 * @param {Object} options.fetchParams - Parameters to pass to fetchFn
 * @param {number} options.priority - Request priority (higher = more important, default 0)
 * @param {boolean} options.enableBatching - Whether to enable batching (default true)
 * @param {boolean} options.autoFetch - Whether to fetch automatically on mount (default true)
 * @param {number} options.cacheTTL - Cache TTL in milliseconds (optional, uses default if not provided)
 * @returns {Object} Reactive data, loading, error, and refresh function
 */
export function useWidgetData(options = {}) {
  const {
    dataKey,
    fetchFn,
    fetchParams = {},
    priority = 0,
    autoFetch = true,
    cacheTTL = null
  } = options

  // Auto-disable batching for top section widgets so they load independently and fast
  // Widgets in section 0 (top) skip batching; lower sections batch for efficiency
  const sectionIndex = inject('sectionIndex', null)
  const isTopSection = sectionIndex === 0
  const enableBatching = options.enableBatching !== undefined
    ? options.enableBatching
    : !isTopSection
  if (!dataKey) {
    throw new Error('useWidgetData: dataKey is required')
  }

  if (!fetchFn || typeof fetchFn !== 'function') {
    throw new Error('useWidgetData: fetchFn must be a function')
  }

  // Unwrap dataKey if it's a computed/ref
  const getDataKeyValue = () => {
    if (typeof dataKey === 'function') {
      return dataKey()
    }
    if (dataKey && typeof dataKey === 'object' && 'value' in dataKey) {
      return dataKey.value
    }
    return dataKey
  }

  // Get or create shared store - track dataKey changes
  const currentDataKey = computed(() => getDataKeyValue())
  
  // Register this widget as a subscriber
  const widgetId = Symbol('widget')
  let currentStore = getSharedStore(currentDataKey.value)
  currentStore.subscribers.add(widgetId)

  // Watch for dataKey changes and update store
  watch(currentDataKey, (newKey, oldKey) => {
    if (oldKey && newKey !== oldKey) {
      // Unsubscribe from old store
      const oldStore = getSharedStore(oldKey)
      oldStore.subscribers.delete(widgetId)
    }
    // Subscribe to new store
    currentStore = getSharedStore(newKey)
    currentStore.subscribers.add(widgetId)
  })

  // Factory: create a fetchFn that updates a specific store (not a closed-over store).
  // Required because when dataKey changes (e.g. today -> last_7_days), the new store
  // needs its own fetchFn - copying the old one would write to the wrong store.
  const createStoreFetchFn = (store) => async () => {
    const isBackgroundRefresh = store.data.value != null
    try {
      if (!isBackgroundRefresh) {
        store.loading.value = true
      }
      store.error.value = null

      const params = typeof fetchParams === 'function'
        ? fetchParams()
        : (fetchParams && typeof fetchParams === 'object' && 'value' in fetchParams
          ? fetchParams.value
          : fetchParams)

      // Pass batchGroup (section index) so queries in the same section batch together
      const batchGroup = sectionIndex != null ? `section-${sectionIndex}` : null
      const result = await fetchFn(params, priority, enableBatching, batchGroup)

      store.data.value = result
      store.error.value = null
      store.lastFetch = Date.now()

      return result
    } catch (err) {
      store.error.value = err
      store.data.value = null
      throw err
    } finally {
      store.loading.value = false
    }
  }

  // Always update the store's fetchFn so the latest fetchParams closure is used
  // (component remounts create new refs, so the old closure would be stale)
  currentStore.fetchFn = createStoreFetchFn(currentStore)
  
  /**
   * Refreshes the data
   * @param {boolean} force - Force refresh even if cached
   * @param {Object} [options] - Additional options
   * @param {boolean} [options.showLoading] - Force loading state (clear data so skeleton shows)
   */
  const refresh = async (force = false, options = {}) => {
    if (force) {
      dataFetcher.invalidateCache()
    }
    const activeStore = getSharedStore(currentDataKey.value)
    if (options.showLoading) {
      activeStore.data.value = null
    }
    if (!activeStore.fetchFn) {
      activeStore.fetchFn = createStoreFetchFn(activeStore)
    }
    return activeStore.fetchFn()
  }
  
  // Watch demo mode if provided and refresh when it changes
  if (options.demoMode) {
    watch(() => options.demoMode.value, (newVal, oldVal) => {
      if (newVal !== oldVal && typeof oldVal !== 'undefined') {
        dataFetcher.invalidateCache()
        if (autoFetch) {
          const activeStore = getSharedStore(currentDataKey.value)
          if (!activeStore.fetchFn) {
            activeStore.fetchFn = createStoreFetchFn(activeStore)
          }
          activeStore.fetchFn()
        }
      }
    }, { immediate: false })
  }

  // Auto-fetch on mount if enabled
  onMounted(() => {
    if (autoFetch) {
      const activeStore = getSharedStore(currentDataKey.value)
      if (!activeStore.data.value && !activeStore.loading.value) {
        if (!activeStore.fetchFn) {
          activeStore.fetchFn = createStoreFetchFn(activeStore)
        }
        activeStore.fetchFn()
      }
    }
  })

  // Cleanup on unmount
  const cleanup = () => {
    const activeStore = getSharedStore(currentDataKey.value)
    activeStore.subscribers.delete(widgetId)
    // If no more subscribers, we could optionally clear the store
    // But keeping it allows for instant re-subscription
  }

  // Watch for changes in fetchParams and refetch if needed
  const runFetch = () => {
    if (!autoFetch) return
    nextTick(() => {
      const activeStore = getSharedStore(currentDataKey.value)
      if (!activeStore.fetchFn) {
        activeStore.fetchFn = createStoreFetchFn(activeStore)
      }
      activeStore.fetchFn()
    })
  }
  if (typeof fetchParams === 'object' && fetchParams !== null && 'value' in fetchParams) {
    watch(fetchParams, runFetch, { deep: true, flush: 'post' })
  } else if (typeof fetchParams === 'function') {
    watch(fetchParams, runFetch, { deep: true, flush: 'post' })
  } else {
    watch(() => fetchParams, runFetch, { deep: true, flush: 'post' })
  }

  return {
    data: computed(() => {
      const activeStore = getSharedStore(currentDataKey.value)
      return activeStore.data.value
    }),
    loading: computed(() => {
      const activeStore = getSharedStore(currentDataKey.value)
      return activeStore.loading.value
    }),
    error: computed(() => {
      const activeStore = getSharedStore(currentDataKey.value)
      return activeStore.error.value
    }),
    refresh,
    cleanup
  }
}

/**
 * Helper function for GraphQL queries
 * @param {Object} options - Configuration options
 * @param {string} options.dataKey - Unique key for this data source
 * @param {string} options.query - GraphQL query string
 * @param {Object} options.variables - GraphQL variables
 * @param {string} options.operationName - GraphQL operation name
 * @param {number} options.priority - Request priority
 * @param {boolean} options.enableBatching - Enable batching
 * @param {boolean} options.autoFetch - Auto fetch on mount
 * @returns {Object} Reactive data, loading, error, and refresh function
 */
export function useGraphQLData(options = {}) {
  const {
    dataKey,
    query,
    variables = {},
    operationName = null,
    priority = 0,
    autoFetch = true,
    getDemoData = null
  } = options

  if (!query) {
    throw new Error('useGraphQLData: query is required')
  }

  const demoMode = getDemoData ? useDemoMode() : null

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (getDemoData && demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(getDemoData()), 300)
      })
    }
    const finalVariables = params || {}
    return dataFetcher.fetchGraphQL(
      query,
      finalVariables,
      operationName,
      requestPriority ?? priority,
      enableBatch ?? options.enableBatching,
      batchGroup
    )
  }

  // Only pass enableBatching to useWidgetData if explicitly set by the caller
  const widgetDataOptions = {
    dataKey,
    fetchFn,
    fetchParams: variables,
    priority,
    autoFetch,
    demoMode: demoMode || undefined
  }
  if (options.enableBatching !== undefined) {
    widgetDataOptions.enableBatching = options.enableBatching
  }
  return useWidgetData(widgetDataOptions)
}

/**
 * Helper function for fetching bookings data
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive bookings data
 */
export function useBookingsData(options = {}) {
  const query = `
    query GetBookings($limit: Int, $offset: Int) {
      bookings(limit: $limit, offset: $offset) {
        id
        name
        time
        image
        space_id
        start_time
        end_time
      }
    }
  `

  return useGraphQLData({
    dataKey: 'bookings',
    query,
    variables: options.variables || {},
    priority: options.priority || 1,
    ...options
  })
}

/**
 * Helper function for fetching assignments data
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive assignments data
 */
export function useAssignmentsData(options = {}) {
  const query = `
    query GetAssignments($limit: Int, $offset: Int) {
      assignments(limit: $limit, offset: $offset) {
        id
        name
        space_id
        start_time
        end_time
      }
    }
  `

  return useGraphQLData({
    dataKey: 'assignments',
    query,
    variables: options.variables || {},
    priority: options.priority || 1,
    ...options
  })
}

/**
 * Helper function for fetching users data
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive users data
 */
export function useUsersData(options = {}) {
  const demoMode = useDemoMode()
  const query = `
    query GetUsers($limit: Int, $offset: Int) {
      users(limit: $limit, offset: $offset) {
        id
        name
        email
        status
      }
    }
  `

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getDemoUsersData())
        }, 300)
      })
    }
    return dataFetcher.fetchGraphQL(
      query,
      params || options.variables || {},
      null,
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  return useWidgetData({
    dataKey: 'users',
    fetchFn,
    fetchParams: options.variables || {},
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching time saved data
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive time saved data
 */
export function useTimeSavedData(options = {}) {
  const demoMode = useDemoMode()
  const workflowLimit = usePreviewLimit(1000)

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const isPrevious = params?.isPreviousPeriod === true
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getDemoTimeSavedData(isPrevious ? 'previous' : 'current'))
        }, 300)
      })
    }

    // Build query with date range parameters for enrollment_count.
    // Pass values as GraphQL variables (not inline) so the query string is
    // identical across calls regardless of the timestamp values.
    const timestampFrom = params?.from_timestamp
    const timestampTo = params?.to_timestamp
    const hasDateRange = timestampFrom !== undefined && timestampTo !== undefined
    const varDeclarations = hasDateRange
      ? '$limit: Int, $created_timestamp_from: Int, $created_timestamp_to: Int'
      : '$limit: Int'
    const enrollmentCountArgs = hasDateRange
      ? '(created_timestamp_from: $created_timestamp_from, created_timestamp_to: $created_timestamp_to)'
      : ''

    const query = `
      query getTotalAutomations(${varDeclarations}) {
        workflows(order: NAME_ASC, limit: $limit) {
          total
          data {
            workflow_id
            name
            time_saving_minutes
            is_default
            enrollment_count${enrollmentCountArgs}
          }
        }
      }
    `

    const gqlVariables = { limit: workflowLimit }
    if (hasDateRange) {
      gqlVariables.created_timestamp_from = timestampFrom
      gqlVariables.created_timestamp_to = timestampTo
    }

    const result = await dataFetcher.fetchGraphQL(
      query,
      gqlVariables,
      'getTotalAutomations',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )

    // Transform workflows data to enrollments format
    // Calculate total minutes saved: time_saving_minutes * enrollment_count
    // Sort by time saved (highest first)
    if (result?.workflows?.data) {
      const enrollments = result.workflows.data
        .map(workflow => ({
          workflowId: workflow.workflow_id,
          name: workflow.name,
          minutesSaved: (workflow.time_saving_minutes || 0) * (workflow.enrollment_count || 0),
          count: workflow.enrollment_count || 0,
          isDefault: workflow.is_default || false
        }))
        .sort((a, b) => b.minutesSaved - a.minutesSaved) // Sort descending by minutes saved

      return {
        enrollments
      }
    }

    return { enrollments: [] }
  }

  // Handle reactive variables - convert dateRange to timestamps
  const fetchParams = computed(() => {
    const base = { isPreviousPeriod: options.isPreviousPeriod || false }
    let dateRange = 'last_7_days'
    if (options.variables && typeof options.variables === 'function') {
      dateRange = options.variables()?.dateRange || 'last_7_days'
    } else if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      dateRange = options.variables.value?.dateRange || 'last_7_days'
    } else if (options.variables?.dateRange) {
      dateRange = options.variables.dateRange
    }

    const timestamps = base.isPreviousPeriod
      ? getPreviousPeriodTimestamps(dateRange)
      : getDateRangeTimestamps(dateRange)

    return {
      ...base,
      from_timestamp: timestamps.from_timestamp,
      to_timestamp: timestamps.to_timestamp
    }
  })

  // Use a dynamic dataKey that includes the timestamps so current/previous periods are cached separately
  const dataKey = computed(() => {
    const vars = fetchParams.value
    return `${options.dataKey || 'timeSaved'}_${vars.from_timestamp}_${vars.to_timestamp}`
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching schedule timeline data
 * @param {Object} options - Configuration options
 * @param {Array<string>} options.location_id - Location IDs (optional)
 * @param {number} options.from_timestamp - Start timestamp (Unix seconds)
 * @param {number} options.to_timestamp - End timestamp (Unix seconds)
 * @param {number} options.limit - Limit results (optional)
 * @param {number} options.page - Page number (optional)
 * @returns {Object} Reactive schedule data
 */
export function useScheduleData(options = {}) {
  const demoMode = useDemoMode()
  
  const query = `
    query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
      schedule(
        location_id: $location_id
        start_timestamp_to: $to_timestamp
        end_timestamp_from: $from_timestamp
        limit: $limit
        page: $page
      ) {
        total
        data {
          type
          assignment_id
          availability_block_id
          booking_id
          is_recurring
          title
          resource {
            resource_id
            name
            type {
              name
              __typename
            }
            main_image {
              url
              __typename
            }
            location {
              location_id
              name
              timezone
              timezone_name
              __typename
            }
            parent_resources {
              resource_id
              name
              main_image {
                url
                __typename
              }
              __typename
            }
            child_resources {
              resource_id
              name
              main_image {
                url
                __typename
              }
              __typename
            }
            __typename
          }
          location {
            location_id
            name
            timezone
            timezone_name
            __typename
          }
          status
          start_timestamp
          end_timestamp
          time_intervals(from_timestamp: $from_timestamp, to_timestamp: $to_timestamp) {
            start_timestamp
            end_timestamp
            __typename
          }
          owner_account {
            account_id
            name
            type
            billing_email
            avatar {
              url
              __typename
            }
            __typename
          }
          tour_id
          __typename
        }
        __typename
      }
    }
  `

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return options.variables || {}
  })

  // Generate unique data key based on filter type and date range
  // Include date range in dataKey to ensure different date ranges get separate cache entries
  const dataKeyBase = options.filterType 
    ? `schedule_${options.filterType}` 
    : 'schedule'
  
  // Create a computed dataKey that includes date range hash
  // This ensures different date ranges get different cache entries
  const dataKey = computed(() => {
    const variables = fetchParams.value
    
    // Create a hash of the date range for the dataKey
    const dateRangeHash = variables.from_timestamp && variables.to_timestamp
      ? `${variables.from_timestamp}_${variables.to_timestamp}`
      : 'default'
    
    return `${dataKeyBase}_${dateRangeHash}`
  })

  // Create fetch function that uses demo data when demo mode is enabled
  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    // Get current variables (could be reactive)
    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }
    
    // Check demo mode value at call time (always check current value)
    const isDemoMode = demoMode ? (demoMode.value === true) : false
    
    if (isDemoMode) {
      // Return demo data immediately
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getDemoScheduleData({
            from_timestamp: variables.from_timestamp,
            to_timestamp: variables.to_timestamp
          }))
        }, 300) // Small delay to simulate API call
      })
    } else {
      // Use real API
      return dataFetcher.fetchGraphQL(
        query,
        variables,
        null,
        requestPriority ?? options.priority ?? 2,
        enableBatch ?? options.enableBatching ?? true,
        batchGroup
      )
    }
  }

  // Use a function to get the current dataKey value since it's computed
  const getDataKey = () => dataKey.value
  
  return useWidgetData({
    dataKey: getDataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 2,
    enableBatching: false, // Disable batching when using demo mode
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode // Pass demo mode so it can be watched
  })
}

/**
 * Helper function for fetching custom automations count
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive custom automations data
 */
export function useCustomAutomationsData(options = {}) {
  const demoMode = useDemoMode()
  const query = `
    query GetNonDefaultAutomationCount {
      workflows(order: NAME_ASC search: { is_default: false }) {
        total
      }
    }
  `

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(getDemoCustomAutomationsData()), 300)
      })
    }
    return dataFetcher.fetchGraphQL(
      query,
      {},
      'GetNonDefaultAutomationCount',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  return useWidgetData({
    dataKey: 'customAutomations',
    fetchFn,
    fetchParams: {},
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching recent automation enrollments
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive recent automation enrollments data
 */
export function useRecentAutomationEnrollmentsData(options = {}) {
  const demoMode = useDemoMode()
  const query = `
    query recentAutomationEnrollments {
      workflowEnrollments(order: CREATED_TIMESTAMP_DESC) {
        total
        data {
          workflow_enrollment_id
          status
          workflow {
            name
            workflow_id
          }
          account {
            name
            avatar {
              url
            }
          }
          created_timestamp
        }
      }
    }
  `

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(getDemoRecentAutomationEnrollmentsData())
        }, 300)
      })
    }
    return dataFetcher.fetchGraphQL(
      query,
      {},
      'recentAutomationEnrollments',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  return useWidgetData({
    dataKey: 'recentAutomationEnrollments',
    fetchFn,
    fetchParams: {},
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching most popular automations by enrollment count
 * Uses the workflows API with enrollment_count for server-side date filtering
 * @param {Object} options - Configuration options
 * @param {number} options.timestamp_from - Start timestamp (Unix seconds)
 * @param {number} options.timestamp_to - End timestamp (Unix seconds)
 * @returns {Object} Reactive most popular automations data
 */
export function useMostPopularAutomationsData(options = {}) {
  const demoMode = useDemoMode()

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            workflows: [
              { workflowId: '1', name: 'Welcome Email', enrollmentCount: 45 },
              { workflowId: '2', name: 'Onboarding Sequence', enrollmentCount: 32 },
              { workflowId: '3', name: 'Check-in Reminder', enrollmentCount: 28 },
              { workflowId: '4', name: 'Payment Reminder', enrollmentCount: 19 },
              { workflowId: '5', name: 'Tour Follow-up', enrollmentCount: 6 }
            ]
          })
        }, 300)
      })
    }

    // Get current variables (could be reactive)
    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    // Build query with date range parameters for enrollment_count.
    // Pass values as GraphQL variables (not inline) so the query string is
    // identical across calls regardless of the timestamp values.
    const timestampFrom = variables.timestamp_from
    const timestampTo = variables.timestamp_to
    const hasDateRange = timestampFrom !== undefined && timestampTo !== undefined
    const varDeclarations = hasDateRange
      ? '($created_timestamp_from: Int, $created_timestamp_to: Int)'
      : ''
    const enrollmentCountArgs = hasDateRange
      ? '(created_timestamp_from: $created_timestamp_from, created_timestamp_to: $created_timestamp_to)'
      : ''

    const query = `
      query mostPopularAutomations${varDeclarations} {
        workflows {
          data {
            workflow_id
            name
            enrollment_count${enrollmentCountArgs}
          }
        }
      }
    `

    const gqlVariables = {}
    if (hasDateRange) {
      gqlVariables.created_timestamp_from = timestampFrom
      gqlVariables.created_timestamp_to = timestampTo
    }

    const result = await dataFetcher.fetchGraphQL(
      query,
      gqlVariables,
      'mostPopularAutomations',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )

    if (result?.workflows?.data) {
      const workflows = result.workflows.data.map(workflow => ({
        workflowId: workflow.workflow_id,
        name: workflow.name || 'Unknown Workflow',
        enrollmentCount: workflow.enrollment_count || 0
      }))

      return { workflows }
    }

    return { workflows: [] }
  }

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return options.variables || {}
  })

  return useWidgetData({
    dataKey: 'mostPopularAutomations',
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching check-ins data
 * @param {Object} options - Configuration options
 * @param {number} options.timestamp_from - Start timestamp (Unix seconds)
 * @param {number} options.timestamp_to - End timestamp (Unix seconds)
 * @returns {Object} Reactive check-ins data
 */
export function useCheckInsData(options = {}) {
  const demoMode = useDemoMode()
  const query = `
    query numberOfCheckins($timestamp_from: Int!, $timestamp_to: Int!, $user_id: [ID!], $location_id: [ID!]) {
      checkins(timestamp_from: $timestamp_from, timestamp_to: $timestamp_to, user_id: $user_id, location_id: $location_id) {
        total
      }
    }
  `

  // Default to today's date range if not provided
  const getDefaultTimestamps = () => {
    const now = new Date()
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    return {
      timestamp_from: Math.floor(startOfDay.getTime() / 1000),
      timestamp_to: Math.floor(endOfDay.getTime() / 1000)
    }
  }

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    // Get current variables (could be reactive)
    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    if (demoMode && demoMode.value === true) {
      const preset = options.dateRangePreset?.value ?? options.dateRangePreset
      const isPrevious = (preset && String(preset).includes('previous')) || (options.dataKey || '').includes('previous')
      return new Promise((resolve) => {
        setTimeout(() => resolve(getDemoCheckInsData(isPrevious ? 'previous' : 'current')), 300)
      })
    }

    // Use provided timestamps or default to today
    const defaultTimestamps = getDefaultTimestamps()
    const finalVariables = {
      timestamp_from: variables.timestamp_from ?? defaultTimestamps.timestamp_from,
      timestamp_to: variables.timestamp_to ?? defaultTimestamps.timestamp_to,
      user_id: variables.user_id ?? [],
      location_id: variables.location_id ?? []
    }

    return dataFetcher.fetchGraphQL(
      query,
      finalVariables,
      'numberOfCheckins',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      const vars = options.variables()
      return {
        ...getDefaultTimestamps(),
        user_id: [],
        location_id: [],
        ...vars
      }
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return {
        ...getDefaultTimestamps(),
        user_id: [],
        location_id: [],
        ...options.variables.value
      }
    }
    return {
      ...getDefaultTimestamps(),
      user_id: [],
      location_id: [],
      ...(options.variables || {})
    }
  })

  // Use stable preset-based dataKey so each date range gets its own store.
  // Must NOT use timestamps - they change every second (rolling window) causing
  // the widget to read from an empty store and show 0 despite API returning data.
  const dataKey = computed(() => {
    const preset = options.dateRangePreset?.value ?? options.dateRangePreset
    return preset ? `checkins_${preset}` : 'checkins'
  })

  return useWidgetData({
    dataKey: () => dataKey.value,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching account plans data
 * @param {Object} options - Configuration options
 * @param {number} options.start_timestamp_from - Start timestamp (Unix seconds)
 * @param {number} options.start_timestamp_to - End timestamp (Unix seconds)
 * @returns {Object} Reactive account plans data
 */
export function useAccountPlansData(options = {}) {
  const demoMode = useDemoMode()
  const query = `
    query accountPlans($start_timestamp_from: Int!, $start_timestamp_to: Int!) {
      accountPlans(
        order: START_TIMESTAMP_DESC
        start_timestamp_from: $start_timestamp_from
        start_timestamp_to: $start_timestamp_to
      ) {
        total
        found_without_filters
        data {
          account_plan_id
          plan_template {
            image {
              url
            }
          }
          payer_account {
            account_id
            name
            type
            avatar {
              url
            }
          }
          name
          status
          start_timestamp
          end_timestamp
        }
      }
    }
  `

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(getDemoAccountPlansData('current')), 300)
      })
    }

    // Get current variables (could be reactive)
    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    return dataFetcher.fetchGraphQL(
      query,
      variables,
      'accountPlans',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return options.variables || {}
  })

  return useWidgetData({
    dataKey: 'accountPlans',
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching automation actions count (chargeable)
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive automation actions data
 */
export function useActionsThisPeriodData(options = {}) {
  const demoMode = useDemoMode()
  const query = `
    query automationActionsCount {
      workflowActionsCount {
        total
        chargeable
        data {
          workflow {
            workflow_id
            name
          }
          total
          chargeable
        }
      }
    }
  `

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      return new Promise((resolve) => {
        setTimeout(() => resolve(getDemoActionsThisPeriodData()), 300)
      })
    }
    return dataFetcher.fetchGraphQL(
      query,
      {},
      'automationActionsCount',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  return useWidgetData({
    dataKey: 'actionsThisPeriod',
    fetchFn,
    fetchParams: {},
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching invoices paid data
 * @param {Object} options - Configuration options
 * @param {string} options.dateRange - Date range preset (e.g., 'last_7_days', 'last_30_days')
 * @returns {Object} Reactive invoices paid data
 */
export function useInvoicesPaidData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'invoicesPaid'
  const invoiceLimit = usePreviewLimit(1000)

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const vars = typeof params === 'function' ? params() : (params?.value ?? params)
      const isPrevious = vars?.previousPeriod === true
      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true
      const [demoResult, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoInvoicesPaidData(isPrevious ? 'previous' : 'current')), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      return { ...demoResult, ...orgCurrency }
    }

    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    let fromTimestamp, toTimestamp
    if (variables.previousPeriod && variables.fromDate && variables.toDate) {
      fromTimestamp = variables.fromDate
      toTimestamp = variables.toDate
    } else {
      const dateRange = variables.dateRange || options.dateRange || 'last_30_days'
      const timestamps = getDateRangeTimestamps(dateRange)
      fromTimestamp = timestamps.from_timestamp
      toTimestamp = timestamps.to_timestamp
    }

    const query = `
      query InvoicesPaid($due_from: Int, $due_to: Int, $limit: Int) {
        paid: invoices(due_from: $due_from, due_to: $due_to, status: [PAID], limit: $limit) {
          data { total subtotal tax }
        }
      }
    `

    const priority = requestPriority ?? options.priority ?? 1
    const batch = enableBatch ?? options.enableBatching ?? true

    const [result, orgCurrency] = await Promise.all([
      dataFetcher.fetchGraphQL(
        query,
        { due_from: fromTimestamp, due_to: toTimestamp, limit: invoiceLimit },
        'InvoicesPaid',
        priority,
        batch,
        batchGroup
      ),
      fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
    ])

    // Sum totals to match the format the widget expects
    const data = result?.paid?.data || []
    const excludeTax = variables.excludeTax === true
    const totalPaid = data.reduce((sum, item) => {
      const amount = excludeTax
        ? (parseFloat(item?.subtotal) || 0)
        : (parseFloat(item?.total) || 0)
      return sum + amount
    }, 0)

    return { invoicesPaidTotal: totalPaid, ...orgCurrency }
  }

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {
      dateRange: options.dateRange || 'last_30_days',
      ...(options.variables || {})
    }
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching revenue collected data.
 *
 * Unlike "Invoices paid" (which sums invoices by their due date), this reads actual
 * PAYMENT transactions by the timestamp the payment happened, giving a true
 * cash-collected metric. Refunds are excluded. Results are also aggregated per account.
 * @param {Object} options - Configuration options
 * @returns {Object} Reactive revenue collected data
 */

/**
 * Per-transaction collected amount, removing tax proportionally when requested.
 * The invoice carries the tax for the whole invoice, so we scale it to the payment
 * (handles partial payments correctly): amount * (1 - tax / total).
 * @param {Object} t - Transaction with { amount, invoice: { total, tax } }
 * @param {boolean} excludeTax
 * @returns {number}
 */
export function revenueCollectedNetAmount(t, excludeTax) {
  const amount = parseFloat(t?.amount) || 0
  if (!excludeTax) return amount
  const invoiceTotal = parseFloat(t?.invoice?.total) || 0
  const invoiceTax = parseFloat(t?.invoice?.tax) || 0
  if (invoiceTotal > 0 && invoiceTax > 0) {
    return amount * (1 - invoiceTax / invoiceTotal)
  }
  return amount
}

/**
 * Aggregate payment transactions into a total and a per-account breakdown.
 * Pure and deterministic - the widget and tests both use this.
 * @param {Array} transactions - PAYMENT transactions
 * @param {boolean} [excludeTax=false]
 * @returns {{ revenueCollectedTotal: number, accounts: Array }}
 */
export function aggregateRevenueCollected(transactions, excludeTax = false) {
  const list = Array.isArray(transactions) ? transactions : []
  let total = 0
  const accountMap = new Map()

  list.forEach((t) => {
    const amount = revenueCollectedNetAmount(t, excludeTax)
    total += amount

    const account = t?.invoice?.account
    const name = account?.name || '(Unknown account)'
    const accountId = account?.account_id || null
    const key = accountId ? String(accountId) : name
    const existing = accountMap.get(key) || {
      account_id: accountId,
      name,
      avatar: account?.avatar?.url || null,
      revenue: 0
    }
    accountMap.set(key, { ...existing, revenue: existing.revenue + amount })
  })

  const accounts = Array.from(accountMap.values()).sort((a, b) => b.revenue - a.revenue)
  return { revenueCollectedTotal: total, accounts }
}

export function useRevenueCollectedData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'revenueCollected'
  const pageLimit = usePreviewLimit(500)

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    const excludeTax = variables.excludeTax === true
    const priority = requestPriority ?? options.priority ?? 1
    const batch = enableBatch ?? options.enableBatching ?? true

    if (demoMode && demoMode.value === true) {
      const isPrevious = variables?.previousPeriod === true
      const [demoResult, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoRevenueCollectedData(isPrevious ? 'previous' : 'current')), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      return { ...demoResult, ...orgCurrency }
    }

    let fromTimestamp, toTimestamp
    if (variables.previousPeriod && variables.fromDate && variables.toDate) {
      fromTimestamp = variables.fromDate
      toTimestamp = variables.toDate
    } else {
      const dateRange = variables.dateRange || options.dateRange || 'last_30_days'
      const timestamps = getDateRangeTimestamps(dateRange)
      fromTimestamp = timestamps.from_timestamp
      toTimestamp = timestamps.to_timestamp
    }

    const query = `
      query RevenueCollected($org: ID!, $from: Int, $to: Int, $limit: Int, $page: Int) {
        transactions(
          organization_id: $org
          timestamp_from: $from
          timestamp_to: $to
          filter_by_type: [PAYMENT]
          filter_by_status: ACTIVE
          limit: $limit
          page: $page
        ) {
          total
          data {
            transaction_id
            amount
            timestamp
            invoice {
              total
              tax
              account {
                account_id
                name
                avatar {
                  url
                }
              }
            }
          }
        }
      }
    `

    const [orgId, orgCurrency] = await Promise.all([
      fetchOrgId({ priority, enableBatch: batch, batchGroup }),
      fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
    ])

    // organization_id is required (ID!) on the transactions query - fail loudly rather
    // than firing a query that the API will reject.
    if (!orgId) {
      throw new Error('Revenue collected: could not resolve organization id')
    }

    // Page through all payment transactions in the window.
    const allTransactions = []
    let page = 1
    let total = Infinity
    const MAX_PAGES = 50 // safety backstop against runaway pagination
    while (allTransactions.length < total && page <= MAX_PAGES) {
      const result = await dataFetcher.fetchGraphQL(
        query,
        { org: orgId, from: fromTimestamp, to: toTimestamp, limit: pageLimit, page },
        'RevenueCollected',
        priority,
        batch,
        batchGroup
      )
      const tx = result?.transactions
      const data = Array.isArray(tx?.data) ? tx.data : []
      total = typeof tx?.total === 'number' ? tx.total : allTransactions.length + data.length
      allTransactions.push(...data)
      if (data.length < pageLimit || data.length === 0) break
      page += 1
    }

    return { ...aggregateRevenueCollected(allTransactions, excludeTax), ...orgCurrency }
  }

  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {
      dateRange: options.dateRange || 'last_30_days',
      ...(options.variables || {})
    }
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching unpaid invoices data
 * @param {Object} options - Configuration options
 * @param {string} options.dateRange - Date range preset (e.g., 'last_7_days', 'last_30_days')
 * @returns {Object} Reactive unpaid invoices data
 */
export function useUnpaidInvoicesData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'unpaidInvoices'
  const invoiceLimit = usePreviewLimit(1000)

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true
      const [demoResult, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoUnpaidInvoicesData()), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      return { ...demoResult, ...orgCurrency }
    }

    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    const now = new Date()
    const todayEndSec = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime() / 1000)
    const tenYearsAgo = new Date()
    tenYearsAgo.setFullYear(now.getFullYear() - 10)
    const earlyStartSec = Math.floor(tenYearsAgo.getTime() / 1000)

    const query = `
      query UnpaidInvoices($due_from: Int, $due_to: Int, $limit: Int) {
        due: invoices(due_from: $due_from, due_to: $due_to, status: [DUE], limit: $limit) {
          data { total subtotal tax }
        }
        overdue: invoices(due_from: $due_from, due_to: $due_to, status: [OVERDUE], limit: $limit) {
          data { total subtotal tax }
        }
      }
    `

    const priority = requestPriority ?? options.priority ?? 1
    const batch = enableBatch ?? options.enableBatching ?? true

    const [result, orgCurrency] = await Promise.all([
      dataFetcher.fetchGraphQL(
        query,
        { due_from: earlyStartSec, due_to: todayEndSec, limit: invoiceLimit },
        'UnpaidInvoices',
        priority,
        batch,
        batchGroup
      ),
      fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
    ])

    const excludeTax = variables.excludeTax === true
    const field = excludeTax ? 'subtotal' : 'total'
    const dueData = result?.due?.data || []
    const overdueData = result?.overdue?.data || []
    const allInvoices = [...dueData, ...overdueData]
    const totalUnpaid = allInvoices.reduce((sum, item) => sum + (parseFloat(item?.[field]) || 0), 0)

    return {
      unpaidInvoicesTotal: totalUnpaid,
      ...orgCurrency
    }
  }

  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {}
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching credit owed - sums due/overdue invoices with negative balances
 * Uses the invoices query to fetch individual records, then filters client-side for negative totals.
 * @returns {Object} Reactive credit owed data in analyticsQuery format
 */
export function useCreditOwedData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'creditOwed'
  const invoiceLimit = usePreviewLimit(1000)

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true
      const [demoResult, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoCreditOwedData()), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      return { ...demoResult, ...orgCurrency }
    }

    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    // Fetch credit notes (invoices with negative totals) that are due/overdue
    const query = `
      query CreditOwed($limit: Int) {
        due: invoices(status: [DUE], limit: $limit) {
          data { total subtotal tax }
        }
        overdue: invoices(status: [OVERDUE], limit: $limit) {
          data { total subtotal tax }
        }
      }
    `

    const priority = requestPriority ?? options.priority ?? 1
    const batch = enableBatch ?? options.enableBatching ?? true

    const [result, orgCurrency] = await Promise.all([
      dataFetcher.fetchGraphQL(
        query,
        { limit: invoiceLimit },
        'CreditOwed',
        priority,
        batch,
        batchGroup
      ),
      fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
    ])

    const excludeTax = variables.excludeTax === true
    const field = excludeTax ? 'subtotal' : 'total'
    const dueData = result?.due?.data || []
    const overdueData = result?.overdue?.data || []
    const allInvoices = [...dueData, ...overdueData]
    // Credit notes have negative totals
    const creditNotes = allInvoices.filter(item => (parseFloat(item?.total) || 0) < 0)
    const totalCredit = creditNotes.reduce((sum, item) => sum + (parseFloat(item?.[field]) || 0), 0)

    return {
      creditOwedTotal: totalCredit,
      ...orgCurrency
    }
  }

  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {}
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching invoice amounts grouped by status (all 7 statuses)
 * Uses a single batched query with aliases for each status.
 * @returns {Object} Reactive invoices by status data with { statuses, currency, currency_symbol, locale }
 */
export function useInvoicesByStatusData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'invoicesByStatus'
  const invoiceLimit = usePreviewLimit(1000)

  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return { dateRange: options.dateRange || 'last_30_days', ...(options.variables || {}) }
  })

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    let variables
    if (typeof params === 'function') variables = params()
    else if (params && typeof params === 'object' && 'value' in params) variables = params.value
    else variables = params || {}
    const dateRange = variables.dateRange || 'last_30_days'
    const timestamps = getDateRangeTimestamps(dateRange)

    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999)
    const tomorrowStart = new Date(todayEnd)
    tomorrowStart.setSeconds(1)
    const daysSpan = Math.ceil((timestamps.to_timestamp - timestamps.from_timestamp) / (24 * 60 * 60)) || 30
    const upcomingEnd = new Date(tomorrowStart)
    upcomingEnd.setDate(upcomingEnd.getDate() + daysSpan)

    const todayStartSec = Math.floor(todayStart.getTime() / 1000)
    const todayEndSec = Math.floor(todayEnd.getTime() / 1000)
    const tomorrowStartSec = Math.floor(tomorrowStart.getTime() / 1000)
    // Past-only (e.g. last 30 days): range ends today or before → skip Upcoming and Processing
    const isPastOnly = timestamps.to_timestamp <= todayEndSec
    // Future-oriented (e.g. next 30 days): range starts at/after today AND extends beyond today → skip Due, Overdue
    const isFutureOnly = timestamps.from_timestamp >= todayStartSec && timestamps.to_timestamp > todayEndSec

    if (demoMode && demoMode.value === true) {
      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true
      const [demo, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoInvoicesByStatusData()), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      const statuses = demo.invoicesByStatus.statuses.filter((s) => {
        if (isPastOnly && (s.name === 'Upcoming' || s.name === 'Processing')) return false
        if (isFutureOnly && (s.name === 'Due' || s.name === 'Overdue')) return false
        return true
      })
      return { invoicesByStatus: { ...demo.invoicesByStatus, ...orgCurrency, statuses } }
    }

    const upcoming_to = Math.max(timestamps.to_timestamp, Math.floor(upcomingEnd.getTime() / 1000))

    const excludeTax = variables.excludeTax === true
    const sumInvoiceTotals = (inv) => {
      const data = inv?.data
      if (!Array.isArray(data)) return 0
      return data.reduce((sum, item) => {
        const amount = excludeTax
          ? (parseFloat(item?.subtotal) || 0)
          : (parseFloat(item?.total) || 0)
        return sum + amount
      }, 0)
    }

    const queryParts = []
    const variables_ = {
      paid_from: timestamps.from_timestamp,
      paid_to: timestamps.to_timestamp,
      void_from: timestamps.from_timestamp,
      void_to: timestamps.to_timestamp,
      limit: invoiceLimit
    }
    if (!isPastOnly) {
      variables_.processing_from = timestamps.from_timestamp
      variables_.processing_to = timestamps.to_timestamp
    }

    if (!isFutureOnly) {
      // Due invoices: filter by selected date range
      queryParts.push(
        'due: invoices(due_from: $due_from, due_to: $due_to, status: [DUE], limit: $limit) { data { total subtotal tax } }'
      )
      variables_.due_from = timestamps.from_timestamp
      variables_.due_to = timestamps.to_timestamp
      // Overdue invoices: same date range as due
      queryParts.push(
        'overdue: invoices(due_from: $overdue_from, due_to: $overdue_to, status: [OVERDUE], limit: $limit) { data { total subtotal tax } }'
      )
      variables_.overdue_from = timestamps.from_timestamp
      variables_.overdue_to = timestamps.to_timestamp
    }
    if (!isPastOnly) {
      queryParts.push(
        'upcoming: invoices(due_from: $upcoming_from, due_to: $upcoming_to, status: [UPCOMING], limit: $limit) { data { total subtotal tax } }',
        'processing: invoices(due_from: $processing_from, due_to: $processing_to, status: [PROCESSING], limit: $limit) { data { total subtotal tax } }'
      )
      variables_.upcoming_from = Math.floor(tomorrowStart.getTime() / 1000)
      variables_.upcoming_to = upcoming_to
    }
    queryParts.push(
      'paid: invoices(due_from: $paid_from, due_to: $paid_to, status: [PAID], limit: $limit) { data { total subtotal tax } }',
      'void: invoices(due_from: $void_from, due_to: $void_to, status: [VOID], limit: $limit) { data { total subtotal tax } }'
    )

    const varDecls = []
    if (!isFutureOnly) varDecls.push('$due_from: Int', '$due_to: Int', '$overdue_from: Int', '$overdue_to: Int')
    if (!isPastOnly) varDecls.push('$upcoming_from: Int', '$upcoming_to: Int', '$processing_from: Int', '$processing_to: Int')
    varDecls.push('$paid_from: Int', '$paid_to: Int', '$void_from: Int', '$void_to: Int', '$limit: Int')

    const query = `
      query InvoicesByStatus(${varDecls.join(', ')}) {
        ${queryParts.join('\n      ')}
      }
    `

    const priority = requestPriority ?? options.priority ?? 1
    const batch = enableBatch ?? options.enableBatching ?? true

    const [result, orgCurrency] = await Promise.all([
      dataFetcher.fetchGraphQL(
        query,
        variables_,
        'InvoicesByStatus',
        priority,
        batch,
        batchGroup
      ),
      fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
    ])

    const statuses = []
    statuses.push({ name: 'Paid', amount: sumInvoiceTotals(result?.paid) })
    if (!isPastOnly) {
      statuses.push({ name: 'Upcoming', amount: sumInvoiceTotals(result?.upcoming) })
      statuses.push({ name: 'Processing', amount: sumInvoiceTotals(result?.processing) })
    }
    if (!isFutureOnly) {
      statuses.push(
        { name: 'Due', amount: sumInvoiceTotals(result?.due) },
        { name: 'Overdue', amount: sumInvoiceTotals(result?.overdue) }
      )
    }
    statuses.push({ name: 'Void', amount: sumInvoiceTotals(result?.void) })

    return {
      invoicesByStatus: {
        statuses,
        ...orgCurrency
      }
    }
  }

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching top accounts by revenue (invoiced amount)
 * @param {Object} options - Configuration options
 * @param {string} options.dateRange - Date range preset (e.g., 'last_7_days', 'last_30_days')
 * @param {number} options.limit - Number of top accounts to return (default: 10)
 * @returns {Object} Reactive top accounts by revenue data
 */
export function useTopAccountsByRevenueData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'topAccountsByRevenue'
  const invoiceLimit = usePreviewLimit(1000)
  
  const query = `
    query GetInvoices($limit: Int, $page: Int) {
      invoices(
        limit: $limit
        page: $page
      ) {
        total
        data {
          total
          account {
            account_id
            name
            avatar {
              url
            }
          }
          invoice_header
          is_void
          created_timestamp
          due_timestamp
          paid_timestamp
        }
      }
    }
  `

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true
      const [demoResult, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoTopAccountsByRevenueData()), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      return { analyticsQuery: { ...demoResult.analyticsQuery, ...orgCurrency } }
    }

    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    const limit = variables.limit || options.limit || 10

    try {
      const queryVariables = {
        limit: invoiceLimit,
        page: 1
      }

      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true

      const [result, orgCurrency] = await Promise.all([
        dataFetcher.fetchGraphQL(
          query,
          queryVariables,
          'GetInvoices',
          priority,
          batch,
          batchGroup
        ),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])

      // Group invoices by account and sum revenue
      if (result?.invoices?.data && Array.isArray(result.invoices.data)) {
        const accountMap = new Map()

        let processedCount = 0
        let filteredOutCount = 0

        // Filter invoices by void status, then group by account (no date filtering)
        result.invoices.data.forEach((invoice) => {
          // Skip voided invoices
          if (invoice.is_void) {
            filteredOutCount++
            return
          }

          processedCount++

          if (!invoice.account || invoice.total === undefined || invoice.total === null) return

          const accountId = invoice.account.account_id
          const accountName = invoice.account.name || `Account ${accountId}`
          const accountAvatar = invoice.account.avatar?.url || null
          const amount = parseFloat(invoice.total) || 0

          if (accountId) {
            const existing = accountMap.get(accountId) || {
              account_id: accountId,
              name: accountName,
              avatar: accountAvatar,
              revenue: 0
            }
            accountMap.set(accountId, {
              account_id: accountId,
              name: accountName,
              avatar: accountAvatar || existing.avatar, // Keep existing avatar if new one is null
              revenue: existing.revenue + amount
            })
          }
        })

        // Log processing stats

        // Convert to array, sort by revenue descending, and limit
        const topAccounts = Array.from(accountMap.values())
          .sort((a, b) => b.revenue - a.revenue) // Sort by revenue descending
          .slice(0, limit) // Limit to requested number


        // Return in a format similar to analytics query for consistency
        // Include avatar, full name, and account_id for the widget
        return {
          analyticsQuery: {
            ...orgCurrency,
            data: topAccounts.map(account => [
              account.name, // Full name - widget will format it
              account.revenue,
              account.avatar, // Include avatar URL
              account.account_id // Include account_id for opening account panel
            ]),
            total: topAccounts.length
          }
        }
      }

      return {
        analyticsQuery: {
          ...orgCurrency,
          data: [],
          total: 0
        }
      }
    } catch (error) {
      // If direct invoice query fails, we can't group by account with analytics
      // So we'll fetch invoices directly without date filtering and filter in JS
      // OR skip the direct query and use a different approach
      console.warn('Direct invoice query failed:', error)
      
      // Since we can't group by account in analytics, and direct query doesn't support date filtering,
      // we need to fetch all invoices and filter/group in JavaScript
      // This is not ideal but necessary given API limitations
      throw error // Let it fall through - we'll handle this differently
    }
  }

  // Alternative approach: Always use direct invoice query and filter in JavaScript
  // This ensures we get account info and avatars, but requires fetching more data
  const fetchFnAlternative = async (params, requestPriority, enableBatch) => {
    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    const dateRange = variables.dateRange || options.dateRange || 'last_30_days'
    const timestamps = getDateRangeFromPreset(dateRange)
    const startTimestamp = timestamps.from_timestamp
    const endTimestamp = timestamps.to_timestamp
    const limit = variables.limit || options.limit || 10

    // Fetch invoices directly - we'll filter by date in JavaScript
    const queryVariables = {
      limit: 2000, // Fetch enough to cover the date range
      page: 1
    }

    try {
      const result = await dataFetcher.fetchGraphQL(
        query,
        queryVariables,
        'GetInvoices',
        requestPriority ?? options.priority ?? 1,
        enableBatch ?? options.enableBatching ?? true,
        batchGroup
      )
      
      if (result?.invoices?.data && Array.isArray(result.invoices.data)) {
        const accountMap = new Map()
        
        // Filter invoices by date range and void status, then group by account
        result.invoices.data.forEach((invoice) => {
          // Skip voided invoices
          if (invoice.is_void) return
          
          // Filter by date range - need to check invoice date
          // Since invoice_header might not be a date, we'll need to check if there's a date field
          // For now, we'll process all invoices if we can't determine the date
          // TODO: Find the correct date field for invoices
          
          if (!invoice.account || invoice.total === undefined || invoice.total === null) return
          
          const accountId = invoice.account.account_id
          const accountName = invoice.account.name || `Account ${accountId}`
          const accountAvatar = invoice.account.avatar?.url || null
          const amount = parseFloat(invoice.total) || 0
          
          if (accountId) {
            const existing = accountMap.get(accountId) || { 
              id: accountId,
              name: accountName, 
              avatar: accountAvatar,
              revenue: 0 
            }
            accountMap.set(accountId, {
              id: accountId,
              name: accountName,
              avatar: accountAvatar || existing.avatar,
              revenue: existing.revenue + amount
            })
          }
        })
        
        const topAccounts = Array.from(accountMap.values())
          .sort((a, b) => b.revenue - a.revenue)
          .slice(0, limit)
        
        return {
          analyticsQuery: {
            currency: 'USD',
            currency_symbol: '$',
            locale: 'en-US',
            data: topAccounts.map(account => [
              account.name,
              account.revenue,
              account.avatar,
              account.id // Include ID for use as key
            ]),
            total: topAccounts.length
          }
        }
      }
      
      return {
        analyticsQuery: {
          currency: 'USD',
          currency_symbol: '$',
          locale: 'en-US',
          data: [],
          total: 0
        }
      }
    } catch (error) {
      console.error('Failed to fetch invoices:', error)
      throw error
    }
  }

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {
      limit: options.limit || 10,
      ...(options.variables || {})
    }
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching plan revenue data
 * @param {Object} options - Configuration options
 * @param {string} options.dateRange - Date range preset (e.g., 'last_7_days', 'last_30_days')
 * @returns {Object} Reactive plan revenue data
 */
export function usePlanRevenueData(options = {}) {
  const demoMode = useDemoMode()
  const dataKey = options.dataKey || 'planRevenue'
  
  const getDateRangeFromPreset = (preset) => {
    return getDateRangeTimestamps(preset)
  }

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const vars = typeof params === 'function' ? params() : (params?.value ?? params)
      const isPrevious = vars?.previousPeriod === true
      const priority = requestPriority ?? options.priority ?? 1
      const batch = enableBatch ?? options.enableBatching ?? true
      const [demoResult, orgCurrency] = await Promise.all([
        new Promise((resolve) => setTimeout(() => resolve(getDemoPlanRevenueData(isPrevious ? 'previous' : 'current')), 300)),
        fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
      ])
      return { analyticsQuery: { ...demoResult.analyticsQuery, ...orgCurrency } }
    }

    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    // Get date range from preset or use provided dates
    let startTimestamp, endTimestamp
    if (variables.previousPeriod && variables.fromDate && variables.toDate) {
      // Use provided previous period dates
      startTimestamp = variables.fromDate
      endTimestamp = variables.toDate
    } else {
      const dateRange = variables.dateRange || options.dateRange || 'last_30_days'
      const timestamps = getDateRangeFromPreset(dateRange)
      startTimestamp = timestamps.from_timestamp
      endTimestamp = timestamps.to_timestamp
    }

    // Fetch account plans with price field and dates
    const accountPlansQuery = `
      query accountPlans($start_timestamp_from: Int!, $start_timestamp_to: Int!) {
        accountPlans(
          start_timestamp_from: $start_timestamp_from
          start_timestamp_to: $start_timestamp_to
        ) {
          total
          data {
            account_plan_id
            name
            price
            start_timestamp
            end_timestamp
            payer_account {
              account_id
              name
            }
          }
        }
      }
    `

    // Fetch plans - we want plans that were active during this period
    // So we need to fetch plans that either:
    // 1. Started during the period, OR
    // 2. Started before but ended after the period start, OR  
    // 3. Started before and haven't ended yet
    // For now, let's fetch a wider range to ensure we get all relevant plans
    // We'll fetch plans that started up to a year before the period (to catch long-running plans)
    const plansStartFrom = startTimestamp - (365 * 24 * 60 * 60) // 1 year before period start

    const priority = requestPriority ?? options.priority ?? 1
    const batch = enableBatch ?? options.enableBatching ?? true

    const [plansResult, orgCurrencyData] = await Promise.all([
      dataFetcher.fetchGraphQL(
        accountPlansQuery,
        {
          start_timestamp_from: plansStartFrom,
          start_timestamp_to: endTimestamp
        },
        'accountPlans',
        priority,
        batch,
        batchGroup
      ),
      fetchOrgCurrency({ priority, enableBatch: batch, batchGroup })
    ])

    const orgCurrency = orgCurrencyData.currency
    const orgCurrencySymbol = orgCurrencyData.currency_symbol
    const orgLocale = orgCurrencyData.locale

    // Process account plans and calculate revenue based on period overlap
    const planMap = new Map()
    
    // Debug logging
    
    // Helper function to calculate overlap between plan period and selected period
    const calculatePeriodOverlap = (planStart, planEnd, periodStart, periodEnd) => {
      // Determine the actual active period for the plan
      const actualPlanStart = planStart ? Math.max(planStart, periodStart) : periodStart
      const actualPlanEnd = planEnd ? Math.min(planEnd, periodEnd) : periodEnd
      
      // If plan ends before period starts or starts after period ends, no overlap
      if (planEnd && planEnd < periodStart) return 0
      if (planStart && planStart > periodEnd) return 0
      
      // Calculate overlap in seconds
      const overlapStart = actualPlanStart
      const overlapEnd = actualPlanEnd
      const overlapSeconds = overlapEnd - overlapStart
      
      // Calculate total period duration in seconds
      const periodDuration = periodEnd - periodStart
      
      // Return the proportion (0 to 1)
      return periodDuration > 0 ? overlapSeconds / periodDuration : 0
    }
    
    if (plansResult?.accountPlans?.data && Array.isArray(plansResult.accountPlans.data)) {
      plansResult.accountPlans.data.forEach((plan) => {
        if (plan.account_plan_id && plan.payer_account?.account_id) {
          const price = parseFloat(plan.price) || 0
          
          // Skip free plans (price is 0, null, or undefined)
          if (price <= 0 || !plan.price) {
            return
          }
          
          const planId = plan.account_plan_id
          const planName = plan.name || 'Unknown Plan'
          const accountName = plan.payer_account.name || 'Unknown Account'
          const planStartTimestamp = plan.start_timestamp || null
          const planEndTimestamp = plan.end_timestamp || null
          
          // Calculate how much of the period the plan was active
          const overlapProportion = calculatePeriodOverlap(
            planStartTimestamp,
            planEndTimestamp,
            startTimestamp,
            endTimestamp
          )
          
          // Skip if plan had no overlap with the period
          if (overlapProportion <= 0) {
            return
          }
          
          // Calculate revenue for this period (proportional to overlap)
          // This is used for total revenue calculations
          const periodRevenue = price * overlapProportion
          
          // Store plan data with dates and calculated revenue
          const existing = planMap.get(planId)
          if (existing) {
            // If this plan has a more recent start timestamp, update it
            if (planStartTimestamp && (!existing.start_timestamp || planStartTimestamp > existing.start_timestamp)) {
              planMap.set(planId, {
                id: planId,
                name: planName,
                accountName: accountName,
                price: price, // Full plan price
                periodRevenue: periodRevenue, // Revenue for this period
                start_timestamp: planStartTimestamp,
                end_timestamp: planEndTimestamp,
                overlapProportion: overlapProportion
              })
            } else {
              // Update revenue if this entry has more overlap
              if (overlapProportion > (existing.overlapProportion || 0)) {
                existing.periodRevenue = periodRevenue
                existing.overlapProportion = overlapProportion
              }
            }
          } else {
            planMap.set(planId, {
              id: planId,
              name: planName,
              accountName: accountName,
              price: price, // Full plan price
              periodRevenue: periodRevenue, // Revenue for this period
              start_timestamp: planStartTimestamp,
              end_timestamp: planEndTimestamp,
              overlapProportion: overlapProportion
            })
          }
        }
      })
    }
     
     // Convert to array format similar to analytics query for consistency
     const plansArray = Array.from(planMap.values())
    
    // Return in a format similar to analytics query for consistency
    // Include start_timestamp, end_timestamp, and periodRevenue in the data array
    return {
      analyticsQuery: {
        currency: orgCurrency,
        currency_symbol: orgCurrencySymbol,
        locale: orgLocale,
        data: plansArray.map(plan => [
          plan.name,              // Plan name
          plan.accountName,       // Account name
          plan.periodRevenue,      // Revenue for this period (proportional)
          plan.id,                // Plan ID
          plan.start_timestamp,   // Start timestamp
          plan.end_timestamp,     // End timestamp (null if ongoing)
          plan.price              // Full plan price
        ]),
        total: plansArray.length
      }
    }
  }

  // Handle reactive variables - watch for changes
  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {
      dateRange: options.dateRange || 'last_30_days',
      ...(options.variables || {})
    }
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority || 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}

/**
 * Helper function for fetching dashboard KPIs (converted leads, conversion rates)
 * Shared by all dashboard KPI widgets (tours, inquiries, drop-ins)
 * @param {Object} options - Configuration options
 * @param {Object} options.variables - { dateRange } or { from_timestamp, to_timestamp }
 * @returns {Object} Reactive dashboard KPIs data
 */
export function useDashboardKPIsData(options = {}) {
  // Full query with account data (for current period display)
  const fullQuery = `
    query dashboardKPIs($from_timestamp: Int, $to_timestamp: Int) {
      dashboardKPIs(from_timestamp: $from_timestamp, to_timestamp: $to_timestamp) {
        kpis {
          name
          value
        }
        inquiry_converted_accounts {
          total
          data {
            account_id
            name
            email
            avatar {
              url
            }
          }
        }
        dropin_converted_accounts {
          total
          data {
            account_id
            name
            email
            avatar {
              url
            }
          }
        }
        tour_converted_accounts {
          total
          data {
            account_id
            name
            email
            avatar {
              url
            }
          }
        }
        tour_bought_pass_or_plan_accounts {
          total
          data {
            account_id
            name
            avatar {
              url
            }
          }
        }
        dropin_made_second_booking_accounts {
          total
          data {
            account_id
            name
            avatar {
              url
            }
          }
        }
        dropin_bought_pass_or_plan_accounts {
          total
          data {
            account_id
            name
            avatar {
              url
            }
          }
        }
      }
    }
  `

  // Lightweight query for previous period (only totals needed for comparison chips)
  const totalsOnlyQuery = `
    query dashboardKPIs($from_timestamp: Int, $to_timestamp: Int) {
      dashboardKPIs(from_timestamp: $from_timestamp, to_timestamp: $to_timestamp) {
        kpis {
          name
          value
        }
        inquiry_converted_accounts { total }
        dropin_converted_accounts { total }
        tour_converted_accounts { total }
        tour_bought_pass_or_plan_accounts { total }
        dropin_made_second_booking_accounts { total }
        dropin_bought_pass_or_plan_accounts { total }
      }
    }
  `

  // Use lightweight query when totalsOnly option is set (for previous period comparisons)
  const query = options.totalsOnly ? totalsOnlyQuery : fullQuery

  const demoMode = useDemoMode()

  const fetchFn = async (params, requestPriority, enableBatch, batchGroup) => {
    if (demoMode && demoMode.value === true) {
      const period = options.totalsOnly ? 'previous' : 'current'
      return new Promise((resolve) => {
        setTimeout(() => resolve(getDemoDashboardKPIsData(period)), 300)
      })
    }

    let variables
    if (typeof params === 'function') {
      variables = params()
    } else if (params && typeof params === 'object' && 'value' in params) {
      variables = params.value
    } else {
      variables = params || options.variables || {}
    }

    const dateRange = variables.dateRange || options.dateRange || 'last_30_days'
    const timestamps = getDateRangeTimestamps(dateRange)
    const queryVariables = {
      from_timestamp: variables.from_timestamp ?? timestamps.from_timestamp,
      to_timestamp: variables.to_timestamp ?? timestamps.to_timestamp
    }

    return dataFetcher.fetchGraphQL(
      query,
      queryVariables,
      'dashboardKPIs',
      requestPriority ?? options.priority ?? 1,
      enableBatch ?? options.enableBatching ?? true,
      batchGroup
    )
  }

  const fetchParams = computed(() => {
    if (options.variables && typeof options.variables === 'function') {
      return options.variables()
    }
    if (options.variables && typeof options.variables === 'object' && 'value' in options.variables) {
      return options.variables.value
    }
    return {
      dateRange: options.dateRange || 'last_30_days',
      ...(options.variables || {})
    }
  })

  const dataKey = computed(() => {
    const vars = fetchParams.value
    if (vars?.from_timestamp != null && vars?.to_timestamp != null) {
      return `dashboardKPIs_${vars.from_timestamp}_${vars.to_timestamp}`
    }
    const dateRange = vars?.dateRange || 'last_30_days'
    const timestamps = getDateRangeTimestamps(dateRange)
    return `dashboardKPIs_${timestamps.from_timestamp}_${timestamps.to_timestamp}`
  })

  return useWidgetData({
    dataKey,
    fetchFn,
    fetchParams,
    priority: options.priority ?? 1,
    autoFetch: options.autoFetch !== false,
    demoMode: demoMode
  })
}
